import { useMemo } from 'react';
import { FeaturedTariffPreviewMode, SellingMode } from '@src/core/buyerApiClient';
import { useAppSelector } from '@src/core/hooks';
import { getSchedulingsTariffPreview } from '@src/domains/tariffs';
import { productScheduleSelector, productSelector, selectedProductTariffHour, tariffsState } from '@src/stores/product';
import {
  computeUnitPrice,
  PreviewPrice,
  type UnitPrice,
} from '@src/domains/products/features/product-booking/core/computeUnitPrice';
import { getCurrentMonthAvailableSchedulings } from '@src/domains/products/features/product-booking/core/getCurrentMonthAvailableSchedulings';
import { resolvePreviewPrice } from '@src/domains/products/features/product-booking/core/resolvePreviewPrice';
import { appInfoSelector } from '@src/stores/app';

export type UseUnitDisplayPriceResult = {
  unitPrice: UnitPrice | null;
  isLoading: boolean;
};

export function useUnitDisplayPrice(widgetId: string, productId: string | undefined): UseUnitDisplayPriceResult {
  const { defaultTariffGroup, dates, loadedKeys } = useAppSelector(productScheduleSelector(productId ?? ''));
  const { listOfTariffsPerType, selectedDate } = useAppSelector(tariffsState(widgetId, productId));
  const selectedHour = useAppSelector(selectedProductTariffHour(widgetId, productId)) ?? null;
  const product = useAppSelector(productSelector(productId ?? ''));
  const featuredTariffPreviewMode = useAppSelector(appInfoSelector)?.featuredTariffPreviewMode ?? null;

  // The `defaultTariffGroup` and the current-month schedulings are fetched in
  // parallel and `defaultTariffGroup` typically lands first. Until the first
  // schedulings range has been loaded, `computeUnitPrice` has nothing to walk
  // and falls back to `defaultTariffGroup`'s minimum — which is frequently
  // higher than the cheapest bookable tariff in the current month. Rendering
  // that intermediate value causes a visible flicker (e.g. R$ 2,00 → R$ 1,00)
  // on initial load. `loadedKeys` is the per-fetch ledger; the default tariff
  // group writes its own `'defaultTariffGroup'` key, so the presence of *any
  // other* key tells us a schedulings range has settled (success or empty).
  const hasLoadedSchedulingsRange = useMemo(
    () => Object.keys(loadedKeys ?? {}).some((key) => key !== 'defaultTariffGroup'),
    [loadedKeys],
  );

  // Current-month price range — used as the "starting from" floor before a
  // date is picked so the displayed price never drops once the user selects a
  // cheaper date within the month. Same primitive as the calendar Cell preview.
  const monthSchedulings = useMemo(() => getCurrentMonthAvailableSchedulings(dates, new Date()), [dates]);

  const monthPriceRange = useMemo(() => {
    if (monthSchedulings.length === 0) return null;

    const minCents = getSchedulingsTariffPreview(monthSchedulings, FeaturedTariffPreviewMode.Lowest);
    const maxCents = getSchedulingsTariffPreview(monthSchedulings, FeaturedTariffPreviewMode.Highest);
    if (minCents == null || maxCents == null) return null;

    return { minCents, maxCents };
  }, [monthSchedulings]);

  const previewPrice = useMemo<PreviewPrice | null>(
    () =>
      selectedDate
        ? null
        : resolvePreviewPrice(
            featuredTariffPreviewMode ?? FeaturedTariffPreviewMode.Default,
            monthSchedulings,
            defaultTariffGroup,
          ),
    [featuredTariffPreviewMode, selectedDate, monthSchedulings, defaultTariffGroup],
  );

  return useMemo<UseUnitDisplayPriceResult>(() => {
    if (!productId || !product) return { unitPrice: null, isLoading: Boolean(productId) };

    // Without-date products have no calendar — the default tariff group is the
    // legitimate (and only) source of the displayed price, so don't gate on
    // schedulings. For every other selling mode, hold the render until the
    // first schedulings range has landed (see comment above).
    const needsSchedulings = product.sellingMode !== SellingMode.WithoutDate;
    if (needsSchedulings && !hasLoadedSchedulingsRange) return { unitPrice: null, isLoading: true };

    return {
      unitPrice: computeUnitPrice({
        defaultTariffGroup,
        listOfTariffsPerType,
        selectedHour,
        monthPriceRange,
        previewPrice,
      }),
      isLoading: false,
    };
  }, [
    productId,
    product,
    hasLoadedSchedulingsRange,
    defaultTariffGroup,
    listOfTariffsPerType,
    selectedHour,
    monthPriceRange,
    previewPrice,
  ]);
}
